import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../services/firebase";

import AssignJobsTab from "./AssignJobsTab";
import ViewHistoryTab from "./ViewHistoryTab";
import CompanySummaryTab from "./CompanySummaryTab";
import EditJobsTab from "./EditJobsTab";
import RecurringRulesTab from "./RecurringRulesTab";

export default function AdminDashboard({ scriptLoaded }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = [
    { key: "assign", label: "Assign Jobs" },
    { key: "recurring", label: "Recurring Jobs" },
    { key: "view-history", label: "View History" },
    { key: "summary", label: "Company Summary" },
    { key: "edit", label: "Edit Jobs" },
  ];
  const tabKeys = tabs.map((tab) => tab.key);
  const getTabFromParams = (params) => {
    const tab = params.get("tab") || "assign";
    return tabKeys.includes(tab) ? tab : "assign";
  };

  const activeTab = getTabFromParams(searchParams);
  const [drivers, setDrivers] = useState([]);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());
    if (params.get("tab") !== activeTab) {
      params.set("tab", activeTab);
      setSearchParams(params, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  useEffect(() => {
    (async () => {
      const dq = query(collection(db, "users"), where("role", "==", "driver"));
      const dSnap = await getDocs(dq);
      const dList = dSnap.docs.map((doc) => {
        const data = doc.data() || {};
        return {
          uid: doc.id,
          email: data.email || "",
          companyId: data.companyId || "",
          companyIds: Array.isArray(data.companyIds) ? data.companyIds : [],
        };
      });
      setDrivers(dList);

      const cSnap = await getDocs(collection(db, "companies"));
      const cList = cSnap.docs.map((d) => ({
        id: d.id,
        name: d.data()?.name || d.id,
      }));
      cList.sort((a, b) => String(a.name || "").localeCompare(b.name || ""));
      setCompanies(cList);
    })();
  }, []);

  const activeTabLabel =
    tabs.find((t) => t.key === activeTab)?.label || "Admin Dashboard";

  const TabButton = ({ t }) => {
    const isActive = activeTab === t.key;

    return (
      <button
        onClick={() => {
          const params = new URLSearchParams(searchParams.toString());
          params.set("tab", t.key);
          setSearchParams(params);
        }}
        className={`inline-flex items-center rounded-lg border px-3.5 py-2 text-sm font-medium transition ${
          isActive
            ? "border-black bg-black text-white shadow-sm"
            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
        }`}
      >
        {t.label}
      </button>
    );
  };

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <div className="w-full px-4 py-4 sm:px-6 sm:py-5 xl:px-8 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-baseline gap-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-600">
              Admin
            </span>
            <h1 className="text-xl font-semibold tracking-tight text-gray-950">
              {activeTabLabel}
            </h1>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {tabs.map((t) => (
              <TabButton key={t.key} t={t} />
            ))}
          </div>
        </div>

        <div className="rounded-[24px] border border-gray-200 bg-white p-3 shadow-sm sm:p-4 xl:p-5">
          {activeTab === "assign" && (
            <AssignJobsTab
              drivers={drivers}
              companies={companies}
              scriptLoaded={scriptLoaded}
            />
          )}

          {activeTab === "recurring" && (
            <RecurringRulesTab drivers={drivers} companies={companies} />
          )}

          {activeTab === "view-history" && (
            <ViewHistoryTab drivers={drivers} companies={companies} />
          )}

          {activeTab === "summary" && (
            <CompanySummaryTab companies={companies} />
          )}

          {activeTab === "edit" && (
            <EditJobsTab drivers={drivers} scriptLoaded={scriptLoaded} />
          )}
        </div>
      </div>
    </div>
  );
}
